import React, { useState, useCallback, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { EngravingPreview } from "@/components/configurator/EngravingPreview";
import { ConfiguratorPanel } from "@/components/configurator/ConfiguratorPanel";

// Product image - Silver plate only
const PLATE_IMAGE = {
  url: "https://customer-assets.emergentagent.com/job_2797146a-0c81-4eb0-92a6-b55a24a86729/artifacts/heobupbt_argent%C3%A9.png",
  zone: {
    top: 72,
    left: 50,
    width: 65,
    height: 35,
    angle: 0,
  }
};

const EngravingConfigurator = () => {
  // State
  const [text, setText] = useState('');
  const [selectedFont, setSelectedFont] = useState('Bodoni');
  // Auto-scale font size based on text length (longer text = smaller font)
  const getAutoFontSize = () => {
    const maxLength = 20;
    const minFontSize = 38;  // Taille minimum (texte long)
    const maxFontSize = 58;  // Taille maximum (texte court)
    const textLength = text.length || 1;
    
    // Calculate font size: starts big, gets smaller as text gets longer
    const scale = 1 - (textLength / maxLength);
    const autoSize = minFontSize + (maxFontSize - minFontSize) * scale;
    return Math.round(Math.max(minFontSize, Math.min(maxFontSize, autoSize)));
  };

  const fontSize = getAutoFontSize();
  const [selectedSymbol, setSelectedSymbol] = useState('none');
  const [symbolPosition, setSymbolPosition] = useState('before');
  const [positionOffset] = useState({ top: 0, left: 0 }); // Fixed centered position
  const [isLoading, setIsLoading] = useState(false);
  const [crystalColors, setCrystalColors] = useState(''); // Couleur des cristaux
  
  // Ref pour la capture d'écran
  const previewRef = useRef(null);

  // Get plate zone with offset
  const adjustedZone = {
    ...PLATE_IMAGE.zone,
    top: PLATE_IMAGE.zone.top + positionOffset.top,
    left: PLATE_IMAGE.zone.left + positionOffset.left,
  };

  // Handle add to cart
  const handleAddToCart = useCallback(async () => {
    const lines = text.split('\n').filter(l => l.trim().length > 0);
    
    if (lines.length === 0) {
      toast.error("Veuillez entrer le texte de la gravure", {
        description: "Le champ de texte est requis",
      });
      return;
    }

    setIsLoading(true);

    try {
      // Line item properties for Shopify
      const lineItemProperties = {
        'Ligne 1': lines[0] || '',
        'Ligne 2': lines[1] || '-',
        'Police': selectedFont,
        'Taille': `${fontSize}px`,
        'Symbole': selectedSymbol === 'none' ? 'Aucun' : selectedSymbol,
        'Position symbole': selectedSymbol !== 'none' ? symbolPosition : '-',
        'Couleur cristaux': crystalColors || '-',
      };

      console.log('📦 Shopify Line Item Properties:', lineItemProperties);

      // Si dans un iframe, envoyer au parent
      if (window.parent !== window) {
        console.log('📤 Envoi postMessage au parent...');
        window.parent.postMessage({
          type: 'ADD_TO_CART',
          properties: lineItemProperties
        }, '*');
        
        toast.success("Configuration enregistrée !", {
          description: `"${lines[0]}" - ${selectedFont}`,
        });
      } 
      // Si directement sur Shopify (pas dans iframe)
      else if (window.location.hostname.includes('shopify') || 
               window.location.hostname.includes('myshopify') ||
               window.location.hostname.includes('equipassion')) {
        console.log('🛒 Ajout direct au panier Shopify...');
        
        // Chercher le variant ID dans la page
        let variantId = null;
        
        // Méthode 1: Chercher dans le formulaire produit
        const variantInput = document.querySelector('input[name="id"]') || 
                            document.querySelector('select[name="id"]');
        if (variantInput) {
          variantId = variantInput.value;
        }
        
        // Méthode 2: Chercher dans l'URL
        if (!variantId) {
          const urlParams = new URLSearchParams(window.location.search);
          variantId = urlParams.get('variant');
        }
        
        // Méthode 3: Variable globale Shopify
        if (!variantId && window.ShopifyAnalytics?.meta?.selectedVariantId) {
          variantId = window.ShopifyAnalytics.meta.selectedVariantId;
        }

        if (!variantId) {
          toast.error("Erreur: Variant ID non trouvé", {
            description: "Contactez le support",
          });
          return;
        }

        console.log('🔵 Variant ID trouvé:', variantId);

        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: parseInt(variantId),
            quantity: 1,
            properties: lineItemProperties
          })
        });

        if (response.ok) {
          const data = await response.json();
          console.log('✅ Ajouté au panier:', data);
          toast.success("Ajouté au panier !", {
            description: `"${lines[0]}" - ${selectedFont}`,
          });
          // Rediriger vers le panier après 1 seconde
          setTimeout(() => {
            window.location.href = '/cart';
          }, 1000);
        } else {
          throw new Error('Erreur ' + response.status);
        }
      }
      // Mode standalone (preview/test)
      else {
        console.log('🧪 Mode test - pas d\'ajout au panier');
        toast.success("Configuration enregistrée !", {
          description: `"${lines[0]}" - ${selectedFont}`,
        });
      }

    } catch (error) {
      console.error('❌ Erreur:', error);
      toast.error("Erreur lors de l'ajout au panier", {
        description: error.message,
      });
    } finally {
      setIsLoading(false);
    }
  }, [text, selectedFont, fontSize, selectedSymbol, symbolPosition, crystalColors]);

  return (
    <div 
      className="min-h-screen bg-white"
      data-testid="engraving-configurator"
    >
      {/* Main Content - Inspired by frontal.netlify.app layout */}
      <main className="min-h-screen">
        <div className="grid grid-cols-1 lg:grid-cols-2 min-h-screen">
          {/* Left Side - Controls */}
          <div 
            className="order-2 lg:order-1 flex flex-col p-6 lg:p-10 xl:p-12 bg-white overflow-y-auto"
            data-testid="controls-area"
          >
            <ConfiguratorPanel
              text={text}
              setText={setText}
              selectedFont={selectedFont}
              setSelectedFont={setSelectedFont}
              selectedSymbol={selectedSymbol}
              setSelectedSymbol={setSelectedSymbol}
              symbolPosition={symbolPosition}
              setSymbolPosition={setSymbolPosition}
              onAddToCart={handleAddToCart}
              isLoading={isLoading}
              maxChars={20}
            />
          </div>

          {/* Right Side - Preview */}
          <div 
            className="order-1 lg:order-2 bg-neutral-50 p-6 lg:p-10 xl:p-12 flex flex-col"
            data-testid="preview-area"
          >
            {/* Header */}
            <div className="text-center mb-6">
              <h1 
                className="text-3xl lg:text-4xl text-neutral-900 tracking-wide"
                style={{ fontFamily: "'Bodoni Moda', serif" }}
                data-testid="header-title"
              >
                Votre gravure
              </h1>
            </div>

            {/* Preview Image */}
            <div className="flex-1 flex items-center justify-center">
              <div className="w-full max-w-xl">
                <EngravingPreview
                  previewImage={PLATE_IMAGE.url}
                  plateZone={adjustedZone}
                  text={text}
                  selectedFont={selectedFont}
                  fontSize={fontSize}
                  selectedSymbol={selectedSymbol}
                  symbolPosition={symbolPosition}
                />
              </div>
            </div>

            {/* Disclaimer text */}
            <p className="text-center text-xs text-neutral-400 mt-4 italic">
              Visuel non contractuel, gravure adaptée à la taille de la plaque et centrée.
            </p>

            {/* Crystal Colors Section - DESKTOP ONLY (hidden on mobile/tablet) */}
            <div className="hidden md:block mt-6">
              <div className="flex items-center gap-4 mb-6">
                <div className="flex-1 h-px bg-neutral-300"></div>
                <span className="text-neutral-400 text-sm font-medium">CRISTAUX</span>
                <div className="flex-1 h-px bg-neutral-300"></div>
              </div>

              <div className="p-5 bg-white rounded-xl border-2 border-amber-200 shadow-sm">
                <h2 
                  className="text-xl font-semibold text-neutral-900 mb-4 text-center"
                  style={{ fontFamily: "'Bodoni Moda', serif" }}
                >
                  Couleur des cristaux
                </h2>
                
                <div className="flex flex-row gap-4">
                  <div className="w-1/2">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_2797146a-0c81-4eb0-92a6-b55a24a86729/artifacts/asi1ozxx_liste%20des%20cristaux.png"
                      alt="Liste des couleurs de cristaux disponibles"
                      className="w-full rounded-lg"
                    />
                  </div>
                  <div className="w-1/2 flex flex-col">
                    <textarea
                      value={crystalColors}
                      onChange={(e) => setCrystalColors(e.target.value)}
                      placeholder="Choisissez plusieurs couleurs de cristal et indiquez l'ordre.

Exemple : or, noir, or, noir...
ou : 2x turquoise, 2x argent...

Soyez le plus précis possible"
                      className="flex-1 w-full min-h-[180px] p-3 text-sm border border-neutral-200 rounded-lg resize-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none"
                      data-testid="crystal-colors-input-desktop"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Crystal Colors Section - MOBILE ONLY (hidden on desktop/tablet) */}
        <div className="md:hidden w-full bg-white border-t border-neutral-200 p-6">
          <h2 
            className="text-xl font-semibold text-neutral-900 mb-4 text-center"
            style={{ fontFamily: "'Bodoni Moda', serif" }}
          >
            Couleur des cristaux
          </h2>
          
          <div className="flex flex-col gap-4">
            <div>
              <img 
                src="https://customer-assets.emergentagent.com/job_2797146a-0c81-4eb0-92a6-b55a24a86729/artifacts/asi1ozxx_liste%20des%20cristaux.png"
                alt="Liste des couleurs de cristaux disponibles"
                className="w-full rounded-lg border border-neutral-200"
              />
            </div>
            <div>
              <textarea
                value={crystalColors}
                onChange={(e) => setCrystalColors(e.target.value)}
                placeholder="Choisissez plusieurs couleurs de cristal et indiquez l'ordre.

Exemple : or, noir, or, noir...
ou : 2x turquoise, 2x argent...

Soyez le plus précis possible"
                className="w-full min-h-[150px] p-3 text-sm border-2 border-amber-200 rounded-lg resize-none focus:border-amber-500 focus:ring-1 focus:ring-amber-500 focus:outline-none"
                data-testid="crystal-colors-input-mobile"
              />
            </div>
          </div>
        </div>
      </main>

      {/* Toast notifications */}
      <Toaster position="top-center" richColors />
    </div>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<EngravingConfigurator />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
